/**
 * Shared layout for the pawn report screens (Payment / Receipt / Settlement /
 * Stock) — the "Payment Report layout", factored out so every report gets it.
 *
 * The rule: the PAGE never scrolls. The screen is a flex column —
 *
 *   page (height: 100%, flex column)
 *     ├── heading            (fixed)
 *     ├── filters card       (fixed)
 *     └── results card       (flex: 1, minHeight: 0)   ← takes what's left
 *           ├── card heading (fixed)
 *           └── TableContainer (flex: 1, minHeight: 0, overflow: auto)
 *
 * so the table gets exactly the leftover height and its sticky totals row lands
 * on the bottom edge of the viewport, whatever the filter card's height happens
 * to be on that report. (This replaced per-report `maxHeight: calc(100vh -
 * 320px)` guesses, which only lined up on the report they were measured for.)
 *
 * On phones the filter cards are tall enough that the leftover can shrink to
 * nothing, so the results card keeps a minimum height there and the page is
 * allowed to scroll instead of squashing the table.
 */

const BG_ALT = "#F1F5F9";

// Minimum table-area height on small screens, where "whatever is left over"
// would otherwise be a couple of rows.
const MIN_RESULTS_H = 340;

/**
 * Outer wrapper of a report screen. `embedded` means it renders inside the
 * unified Reports screen, which already supplies the page chrome — so it just
 * has to flex into the space it was handed.
 */
export const reportPageSx = (embedded) =>
  embedded
    ? {
        p: 0,
        flex: 1,
        // Both axes: a wide table must scroll inside its own TableContainer, not
        // stretch this column and push the page sideways.
        minHeight: 0,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
      }
    : {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        // Vertical stays scrollable rather than `hidden` so a very short window
        // degrades to a scroll instead of clipping rows away. Horizontal is
        // pinned shut: every wide child (tables, chip strips) owns its own
        // sideways scroll, so a page-level one only ever means something has
        // escaped its container and dragged the heading off screen with it.
        overflowY: "auto",
        overflowX: "hidden",
        bgcolor: BG_ALT,
        p: { xs: 1.25, sm: 1.5, md: 2.5 },
      };

/** Anything above the results card (heading, filters, banners, summaries). */
export const reportFixedSx = { flexShrink: 0 };

/**
 * The results card: eats the remaining height and lays its own children out as
 * a column so the TableContainer inside can flex.
 */
export const reportResultsCardSx = {
  flex: 1,
  // The narrower the screen, the more rows the filter card wraps into and the
  // less "leftover" there is — so phones AND tablet portrait keep a floor under
  // the table and let the page scroll instead of squashing it to two rows.
  minHeight: { xs: MIN_RESULTS_H, md: 0 },
  minWidth: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

/**
 * The scrolling table area. Fills the results card; scrollbar hidden so the
 * table reads as part of the page (sticky header + sticky totals still work).
 */
export const reportTableSx = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  scrollbarWidth: "none", // Firefox
  msOverflowStyle: "none", // Legacy Edge/IE
  "&::-webkit-scrollbar": { display: "none" }, // Chrome/Safari
};

/**
 * Same, for the wide registers that legitimately scroll sideways: only the
 * vertical scrollbar is hidden so the horizontal one stays grabbable.
 */
export const reportWideTableSx = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
  scrollbarWidth: "thin", // Firefox can't hide a single axis
  "&::-webkit-scrollbar:vertical": { display: "none" }, // Chrome/Safari
};

/**
 * A row of switcher chips (report type, master type) that must never widen the
 * page. A ToggleButtonGroup won't shrink below its buttons' text, so on a narrow
 * screen the group used to push the whole page sideways and carry the heading
 * off the left edge with it. Here the strip itself scrolls instead — one swipe,
 * no scrollbar chrome, and the page stays put.
 *
 * Spread onto the Box WRAPPING the group, not the group itself.
 */
export const reportSwitcherStripSx = {
  minWidth: 0,
  maxWidth: "100%",
  overflowX: "auto",
  overflowY: "hidden",
  // Nudge the strip's edges to the card padding so a half-scrolled chip doesn't
  // look clipped mid-glyph against a hard border.
  WebkitOverflowScrolling: "touch",
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  "&::-webkit-scrollbar": { display: "none" },
  "& .MuiToggleButtonGroup-root": { flexWrap: "nowrap" },
  "& .MuiToggleButton-root": { flexShrink: 0, whiteSpace: "nowrap" },
};
